import {
  Prompt,
  PromptOperationRequest,
  PromptFormData,
  PromptResponseItem,
  PaginatedPromptsResponse,
} from "@/types";
import { getCurrentUser } from "aws-amplify/auth";

/**
 * 認証ヘッダーを取得
 */
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    await getCurrentUser(); // ユーザーが存在することを確認
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      console.log("🔐 prompts.ts - idToken取得成功");
      return {
        "Content-Type": "application/json",
        "Authorization": idToken,
      };
    } else {
      console.warn("🔐 prompts.ts - idToken取得失敗");
      return {
        "Content-Type": "application/json",
      };
    }
  } catch (error) {
    console.error("🔐 prompts.ts - 認証ヘッダー取得エラー:", error);
    return {
      "Content-Type": "application/json",
    };
  }
};

/**
 * API呼び出しの共通処理
 */
const callApi = async (requestData: PromptOperationRequest) => {
  try {
    const headers = await getAuthHeaders();
    console.log("🔐 prompts.ts - リクエストヘッダー:", headers);
    console.log("🔐 prompts.ts - Authorizationヘッダー存在:", !!headers.Authorization);
    console.log("🔐 prompts.ts - Authorizationヘッダー長さ:", headers.Authorization?.length || 0);

    const response = await fetch("/api/custom-prompts-handler", {
      method: "POST",
      headers,
      body: JSON.stringify(requestData),
    });

    console.log("🔐 prompts.ts - レスポンスステータス:", response.status);
    console.log("🔐 prompts.ts - レスポンスヘッダー:", Object.fromEntries(response.headers.entries()));

    const data = await response.json();

    // データ取得操作で403/404エラーの場合は空データを返す
    if (!response.ok && requestData.operation === "GETALL") {
      if (response.status === 403) {
        console.info(
          "プロンプト取得の権限がありません。空のリストを返します。"
        );
        return {
          message: "データの取得に失敗しました",
          data: [],
          totalItems: 0,
          pagination: {
            currentPage: requestData.page || 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: requestData.limit || 10,
          },
        };
      }
      if (response.status === 404) {
        console.info(
          "プロンプトデータが見つかりませんでした。空のリストを返します。"
        );
        return {
          message: "データが見つかりませんでした",
          data: [],
          totalItems: 0,
          pagination: {
            currentPage: requestData.page || 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: requestData.limit || 10,
          },
        };
      }
    }

    if (!response.ok) {
      throw new Error(data.error || "APIエラーが発生しました");
    }

    return data;
  } catch (error) {
    // データ取得操作でネットワークエラーの場合は空データを返す
    if (error instanceof TypeError && requestData.operation === "GETALL") {
      console.warn(
        "ネットワークエラーまたはAPIが利用できません。空のリストを返します。"
      );
      return {
        message: "データの取得に失敗しました",
        data: [],
        totalItems: 0,
        pagination: {
          currentPage: requestData.page || 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: requestData.limit || 10,
        },
      };
    }
    throw error;
  }
};

/**
 * プロンプト一覧の取得（ページネーション対応）
 */
export const fetchPrompts = async (
  page: number = 1,
  limit: number = 10,
  search?: string
): Promise<PaginatedPromptsResponse> => {
  try {
    const data = await callApi({
      operation: "GETALL",
      page,
      limit,
      search,
    });

    // APIエラーレスポンスをチェック
    if (data.error) {
      console.warn("API側でエラーが発生しました:", data.error);
      return {
        prompts: [],
        pagination: {
          currentPage: page,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: limit,
        },
      };
    }

    // データが存在しない場合の処理
    if (!data.data || !Array.isArray(data.data)) {
      console.info("プロンプトデータが見つかりませんでした。");
      return {
        prompts: [],
        pagination: {
          currentPage: page,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: limit,
        },
      };
    }

    // nameプロパティをprompt_nameにマッピング
    const transformedData = data.data.map((item: PromptResponseItem) => ({
      id: item.id,
      prompt_name: item.prompt_name || "",
      prompt: item.prompt,
      created_at: item.created_at,
    }));

    // ページネーション情報の構築
    const totalItems = data.totalItems || data.data.length;
    const totalPages =
      data.pagination?.totalPages || Math.ceil(totalItems / limit);

    return {
      prompts: transformedData,
      pagination: data.pagination || {
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: limit,
      },
    };
  } catch (error) {
    console.error("プロンプト取得エラー:", error);

    // ネットワークエラーやAPIエラーの場合も空データを返す（サービス継続のため）
    console.warn(
      "プロンプトの取得に失敗しましたが、アプリケーションを継続します。"
    );
    return {
      prompts: [],
      pagination: {
        currentPage: page,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: limit,
      },
    };
  }
};

/**
 * プロンプト一覧の取得（後方互換用）
 */
export const fetchAllPrompts = async (): Promise<Prompt[]> => {
  try {
    const result = await fetchPrompts(1, 1000); // 大きなlimitで全件取得
    return result.prompts;
  } catch (error) {
    console.error("全プロンプト取得エラー:", error);
    console.warn(
      "プロンプトの取得に失敗しましたが、アプリケーションを継続します。"
    );
    return [];
  }
};

/**
 * 新しいプロンプトの追加
 */
export const addPrompt = async (formData: PromptFormData): Promise<void> => {
  // バリデーションチェック
  if (!formData.prompt_name || !formData.prompt) {
    throw new Error("プロンプト名とプロンプト内容は必須です");
  }

  await callApi({
    operation: "REGIST",
    prompt_name: formData.prompt_name,
    prompt: formData.prompt,
  });
};

/**
 * 既存のプロンプトの編集
 */
export const updatePrompt = async (
  id: string,
  formData: PromptFormData
): Promise<void> => {
  // バリデーションチェック
  if (!id) {
    throw new Error("プロンプトIDが必要です");
  }
  if (!formData.prompt_name || !formData.prompt) {
    throw new Error("プロンプト名とプロンプト内容は必須です");
  }

  await callApi({
    operation: "UPDATE",
    id: id,
    prompt_name: formData.prompt_name,
    prompt: formData.prompt,
  });
};

/**
 * プロンプトの削除
 */
export const deletePrompt = async (id: string): Promise<void> => {
  // バリデーションチェック
  if (!id) {
    throw new Error("プロンプトIDが必要です");
  }

  await callApi({
    operation: "DELETE",
    id: id,
  });
};
